import PropTypes from 'prop-types';
import React from 'react';
import { connect, useDispatch } from 'react-redux';
import styled from 'styled-components/macro';
import { getSettingsPages } from './SettingRoutes';
import { openDialog } from 'stash/actions';
import {
	ExternalLinkIcon,
	FormattedDate,
	Link,
	Text,
	UserAvatar,
	useTheme,
} from '@stashinvest/ui';
import { isLocal } from 'stash/utils';
import useLogout from 'stash/hooks/useLogout';

const Mask = styled.div`
	position: fixed;
	background: ${(p) => p.theme.colors.overlay};
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	width: 100%;
	height: 100%;
	opacity: 0;
	z-index: 10;
	visibility: hidden;
	transition: opacity 0.3s, visibility 0.3s 0.3s;

	${({ active }) =>
		active &&
		`
		opacity: 1;
		visibility: visible;
		transition: opacity 0.15s, visibility 0s 0s;
	`};
`;

const Navigation = styled.nav`
	position: fixed;
	width: 80%;
	height: 100%;
	max-width: 25rem;
	${({ isDrawerPositionLeft }) => (isDrawerPositionLeft ? 'left: 0;' : 'right: 0;')}
	top: 0;
	z-index: 10;
	overflow-y: scroll;
	-webkit-overflow-scrolling: touch;
	background: ${(p) => p.theme.colors.bgPrimary};
	transition: transform 0.4s;
	transform: ${({ active }) => !active && 'translate(100%, 0)'};
	display: block;
`;

const DrawerItemLink = styled(Link)`
	text-align: ${({ isDrawerPositionLeft }) => (isDrawerPositionLeft ? 'left' : 'right')};
	display: block;
	padding: ${({ theme }) => `${theme.space.xs} ${theme.space.m}`};
	text-decoration: none;
`;

const DrawerHeaderWrap = styled.div`
	padding: 1.25rem;
	display: flex;
	justify-content: flex-end;
	flex-direction: ${({ isDrawerPositionLeft }) =>
		isDrawerPositionLeft ? 'row-reverse' : 'row'};
	border-bottom: 1px solid ${({ theme }) => theme.colors.borderPrimary};
`;

const UserInfo = styled.div`
	display: inline-block;
	font-weight: 700;
	margin-right: 0.625rem;
	vertical-align: middle;
`;

const StyledAvatar = styled(UserAvatar).attrs({ mt: 'xxs' })`
	border: 2px solid ${({ theme }) => theme.colors.borderPrimary};
	width: 2.8rem;
	height: 2.8rem;
`;

const mapStateToProps = () => ({
	active: true,
});

const DrawerHeader = (props) => (
	<DrawerHeaderWrap isDrawerPositionLeft={props.isDrawerPositionLeft}>
		<UserInfo>
			<Text.H3>{`${props?.user.first_name} ${props?.user.last_name}`}</Text.H3>
			<Text.Title>
				Stasher since{' '}
				<FormattedDate format="MM/yyyy">{props?.user.created_at}</FormattedDate>
			</Text.Title>
		</UserInfo>
		<StyledAvatar backgroundImage={props.avatar} firstName={props?.user.first_name} />
	</DrawerHeaderWrap>
);

DrawerHeader.propTypes = {
	avatar: PropTypes.string,
	user: PropTypes.shape({
		first_name: PropTypes.string,
		last_name: PropTypes.string,
		created_at: PropTypes.instanceOf(Date),
	}),
};

const LogOutItem = (props) => {
	const { colors } = useTheme();
	return (
		<div>
			<DrawerItemLink
				to="/"
				onClick={props.logOut}
				isDrawerPositionLeft={props.isDrawerPositionLeft}
			>
				<Text color={colors.textNegativePrimary}>
					Log out <ExternalLinkIcon />
				</Text>
			</DrawerItemLink>
		</div>
	);
};

LogOutItem.propTypes = {
	logOut: PropTypes.func,
};

const SupportItem = (props) => (
	<div>
		<DrawerItemLink
			href="https://ask.stash.com"
			target="_blank"
			rel="noopener noreferrer"
			isDrawerPositionLeft={props.isDrawerPositionLeft}
		>
			<Text>
				Support <ExternalLinkIcon />
			</Text>
		</DrawerItemLink>
	</div>
);

const DrawerItem = (props) => {
	const isReferral = props.pathname.indexOf('referrals') > -1;
	const pathname = isLocal()
		? `https://app-local.stash.com:57454${props.pathname}`
		: props.pathname;
	return (
		<div>
			<DrawerItemLink
				as={Link}
				onClick={() => {
					window.location.href = pathname;
				}}
				isDrawerPositionLeft={props.isDrawerPositionLeft}
			>
				<Text fontWeight={isReferral ? 'bold' : 'normal'}>{props.title}</Text>
			</DrawerItemLink>
		</div>
	);
};

DrawerItem.propTypes = {
	close: PropTypes.func,
	pathname: PropTypes.string,
	title: PropTypes.string,
};

function MobileSettingsDrawer(props) {
	const { active, close, isDrawerPositionLeft } = props;
	const { logoutUser } = useLogout();
	const dispatch = useDispatch();
	const pages = getSettingsPages(props.showMarketReport);

	const onLogOutClick = (e) => {
		e.preventDefault();
		return dispatch(
			openDialog({
				prompt: 'Are you sure you want to log out?',
				confirmText: 'Log me out',
				cancelText: 'Cancel',
			})
		)
			.then(() => logoutUser())
			.catch(() => {});
	};

	return (
		<div>
			<Mask active={active} onClick={close} />
			<Navigation active={active} isDrawerPositionLeft={isDrawerPositionLeft}>
				<DrawerHeader {...props} />
				{pages.map((page) => (
					<DrawerItem
						key={page.pathname}
						title={page.title}
						pathname={page.pathname}
						close={close}
						isDrawerPositionLeft={isDrawerPositionLeft}
					/>
				))}
				<SupportItem isDrawerPositionLeft={isDrawerPositionLeft} />
				<LogOutItem logOut={onLogOutClick} isDrawerPositionLeft={isDrawerPositionLeft} />
			</Navigation>
		</div>
	);
}

MobileSettingsDrawer.propTypes = {
	active: PropTypes.bool,
	close: PropTypes.func,
	user: PropTypes.object,
	showMarketReport: PropTypes.bool,
	isDrawerPositionLeft: PropTypes.bool,
};

MobileSettingsDrawer.defaultProps = {
	isDrawerPositionLeft: false,
};

export default connect(mapStateToProps, null)(MobileSettingsDrawer);
